import { useEffect } from 'react'
import { Helmet } from '@saas-pages/shared/public/seo'
import { BlogLanguageSwitcher, type BlogLocale } from '@saas-pages/shared/public/blog'
import { Seo, buildBlogPageMetadata } from '@saas-pages/shared/public/seo'
import branding from '../../../branding.config'
import {
  getAllPosts,
  getPostBySlug,
  type BlogPost,
} from '@/shared/blog/posts'
import { useLocale, useT } from '@/shared/i18n'

const BLOG_LOCALE_STORAGE_KEY = 'qrka.blog.locale'

function formatDate(iso: string, locale: 'ru' | 'en'): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function localeHref(post: BlogPost): Partial<Record<BlogLocale, string>> {
  return {
    [post.locale]: `/blog/${post.slug}`,
    ...Object.fromEntries(
      Object.entries(post.translations)
        .filter((entry): entry is [BlogLocale, string] => Boolean(entry[1]))
        .map(([locale, translatedSlug]) => [locale, `/blog/${translatedSlug}`]),
    ),
  }
}

function relatedToolData(t: ReturnType<typeof useT>) {
  return {
    href: '/qr-kod',
    title: t.nav.qrGenerator,
    description: t.blog.relatedToolDescription.qrGenerator,
  }
}

export function BlogPostPage({ slug }: { slug: string }) {
  const t = useT()
  const { setRouteLocale } = useLocale()
  const post = getPostBySlug(slug)

  useEffect(() => {
    if (post) {
      setRouteLocale(post.locale)
      try {
        window.localStorage.setItem(BLOG_LOCALE_STORAGE_KEY, post.locale)
      } catch {
        // localStorage may be blocked by browser settings.
      }
      return () => setRouteLocale(null)
    }
  }, [post, setRouteLocale])

  if (!post) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex" />
          <title>{t.blog.notFoundTitle} - qrka.ru</title>
        </Helmet>
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold tracking-normal">{t.blog.notFoundTitle}</h1>
          <p className="mt-4 text-muted-foreground">
            {t.blog.notFoundDescription}
          </p>
          <a href="/blog" className="mt-6 inline-block text-primary hover:underline">
            {t.common.allBlogPosts}
          </a>
        </div>
      </>
    )
  }

  const metadata = buildBlogPageMetadata(post, getAllPosts(), {
    domain: branding.domain, brandName: branding.domain, defaultOgImage: branding.ogImage,
  })
  const PostContent = post.Component
  const languageHrefs = localeHref(post)
  const relatedTools = post.relatedTools.map(() => relatedToolData(t))

  return (
    <>
      <Seo metadata={metadata} />

      <article className="mx-auto max-w-3xl">
        <nav aria-label={t.common.qrTools} className="mb-6 text-sm text-muted-foreground">
          <a href="/" className="hover:text-foreground">
            qrka.ru
          </a>
          <span className="mx-2">/</span>
          <a href="/blog" className="hover:text-foreground">
            {t.blog.breadcrumb}
          </a>
          <span className="mx-2">/</span>
          <span className="text-foreground">{post.title}</span>
        </nav>

        <header className="mb-8">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md border bg-card px-2 py-0.5">
                {t.blog.localeName[post.locale]}
              </span>
              <time dateTime={post.date}>{formatDate(post.date, post.locale)}</time>
              {post.updated && post.updated !== post.date && (
                <>
                  <span aria-hidden>·</span>
                  <span>
                    {t.common.updated}:{' '}
                    <time dateTime={post.updated}>
                      {formatDate(post.updated, post.locale)}
                    </time>
                  </span>
                </>
              )}
            </div>
            <BlogLanguageSwitcher
              currentLocale={post.locale}
              ariaLabel={t.blog.postLanguageAria}
              labels={t.blog.localeName}
              hrefs={languageHrefs}
              disableMissingHrefs
              onSelectLocale={(next) => setRouteLocale(next)}
            />
          </div>
          <h1 className="text-3xl font-bold tracking-normal sm:text-4xl">
            {post.title}
          </h1>
          <p className="mt-4 leading-7 text-muted-foreground">{post.description}</p>
        </header>

        <div className="prose prose-slate max-w-none prose-headings:tracking-normal prose-headings:text-foreground prose-p:text-foreground/90 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-code:rounded prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none dark:prose-invert">
          <PostContent />
        </div>

        {post.faq.length > 0 && (
          <section className="mt-12 border-t pt-8">
            <h2 className="text-xl font-semibold tracking-normal">{t.common.faq}</h2>
            <div className="mt-4 space-y-4">
              {post.faq.map((item) => (
                <div key={item.q}>
                  <h3 className="font-medium tracking-normal">{item.q}</h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {relatedTools.length > 0 && (
          <section className="mt-12 rounded-lg border bg-muted/30 p-6">
            <h2 className="mb-4 text-base font-semibold tracking-normal">
              {t.blog.relatedTools}
            </h2>
            <ul className="space-y-3">
              {relatedTools.map((tool) => (
                <li key={tool.href}>
                  <a
                    href={tool.href}
                    className="flex items-start gap-3 text-sm hover:text-primary"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background text-xs font-semibold">
                      QR
                    </span>
                    <span>
                      <span className="font-medium">{tool.title}</span>
                      <span className="block text-xs leading-5 text-muted-foreground">
                        {tool.description}
                      </span>
                    </span>
                    <span className="ml-auto self-center text-xs text-muted-foreground">
                      -&gt;
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-10 text-sm">
          <a href="/blog" className="text-primary hover:underline">
            {t.common.allBlogPosts}
          </a>
        </div>
      </article>
    </>
  )
}
